import { getMailingsByIds, MailingRecord } from '@/apis/mailings';
import { AutocompleteResultWithTooltip, isTemplatePlaceholder, MAILING_STATUSES } from '@/types';
import { MailingFilter } from '@/types/searchFilterTypes';
import { createChipLabelText, createChipTooltip } from '@/util';
import { FilterMapper, ValidationContext } from './filterMapperTypes';

const createMailingChips = (mailings: MailingRecord[]) => {
  return mailings.map(({ id, name, scheduledDate = '' }) => {
    const chipTooltipText = createChipTooltip(name, scheduledDate);
    const chipLabel = createChipLabelText(name, scheduledDate);
    return {
      id: id,
      chipLabel: chipLabel,
      label: chipTooltipText,
      tooltipContent: chipTooltipText,
    };
  });
};

export const MailingMapper: FilterMapper = {
  checkIsIncomplete({ value }) {
    const isNoMailingSelected = value[0].mailings.length === 0;
    return isNoMailingSelected;
  },

  toFilter({ value }): MailingFilter {
    if (!value) throw new Error('Missing value');
    // Why an array of length 1? Thats just the way it is the way we use it
    if (value.length !== 1) throw new Error('Invalid value. Expected array of length 1');

    const mailingIds = isTemplatePlaceholder(value[0].mailings)
      ? value[0].mailings
      : (value[0].mailings as AutocompleteResultWithTooltip[]).map(({ id }) => id);
    if (!mailingIds) throw new Error('Missing mailings from value[0]');

    const mailingStatus = value[0].mailingStatus;
    if (!isTemplatePlaceholder(mailingStatus) && !MAILING_STATUSES.includes(mailingStatus))
      throw new Error('Invalid mailing status');

    return {
      name: 'mailing',
      value: {
        mailingIds,
        mailingStatus,
      },
    };
  },

  async fromFilter(filter: MailingFilter, { token }: ValidationContext) {
    const { mailingIds, mailingStatus } = filter.value;
    const isMailingPlaceHolder = isTemplatePlaceholder(mailingIds);

    const mailings = isMailingPlaceHolder ? mailingIds : await getMailingsByIds(token, mailingIds);

    return {
      field: filter.name,
      operator: 'any in',
      value: [
        {
          mailings: isMailingPlaceHolder
            ? mailings
            : createMailingChips(mailings as unknown as MailingRecord[]),
          mailingStatus,
          errors:
            isMailingPlaceHolder ||
            (!isMailingPlaceHolder &&
              mailingIds &&
              mailings &&
              mailingIds.length === (mailings as MailingRecord[]).length)
              ? []
              : [{ field: 'mailings', message: 'Missing mailings' }],
        },
      ],
    };
  },
};
